import {useContext, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Divider, FormControlLabel, Grid, Skeleton, Switch, Typography} from '@mui/material';

import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../contexts/UserContext/UserContext';
import {useApiWrite} from '../../../../hooks/useApiWrite';
import {Patch} from '../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../lib/api/PATCH/PatchRoutes';
import CookieService from '../../../../services/CookieService';
import {PROFILE_SWITCH_SX} from '../../../company/profile/components/profileDialogStyles';
import {useCompanySecuritySettings} from '../../../company/settings/hooks/useCompanySecuritySettings';

export const PersonalTwoFactorAuthentication = () => {
  const {t} = useTranslation('translation', {keyPrefix: 'settings.twoFactorAuthentication'});
  const {currentUser, userContextLogin, isRoleCompany} = useContext(UserContext);
  const {dispatchMessage} = useContext(SnackBarContext);
  const {twoFactor: companyTwoFactor, isLoading: isLoadingCompanySettings} = useCompanySecuritySettings();

  const isEnforced = isRoleCompany && companyTwoFactor.enforced;
  const [enabled, setEnabled] = useState(!!currentUser?.twoFactorEnabled);
  const pendingEnabled = useRef(enabled);

  useEffect(() => {
    setEnabled(!!currentUser?.twoFactorEnabled);
  }, [currentUser?.twoFactorEnabled]);

  // Company enforcement means the user cannot opt out — keep the switch on and locked.
  useEffect(() => {
    if (isEnforced) {
      setEnabled(true);
    }
  }, [isEnforced]);

  const persistEnabled = (nextEnabled: boolean) => {
    if (!currentUser) return;

    const updatedUser = {...currentUser, twoFactorEnabled: nextEnabled};

    CookieService.setCookie(updatedUser);
    userContextLogin(updatedUser);
    setEnabled(nextEnabled);
  };

  const {makeRequest, isLoading: isSaving} = useApiWrite<Patch.UpdateUserTwoFactorAuthentication>({
    method: 'patch',
    path: PatchRoutes[Patch.UpdateUserTwoFactorAuthentication],
    disableLoadingOverlay: true,
    onSuccess: () => {
      persistEnabled(pendingEnabled.current);
      dispatchMessage(t(pendingEnabled.current ? 'enableSuccess' : 'disableSuccess'), 'success');
    },
    onError: () => {
      setEnabled(isEnforced || !!currentUser?.twoFactorEnabled);
      dispatchMessage(t('updateError'), 'error');
    },
  });

  const handleToggle = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (isEnforced || isSaving) return;

    pendingEnabled.current = checked;
    setEnabled(checked);
    makeRequest({user: {twoFactorEnabled: checked}});
  };

  const showCompanyLoading = isRoleCompany && isLoadingCompanySettings;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant='h3'>{t('title')}</Typography>
        <Typography variant='body1' gutterBottom>
          {t('subtitle')}
        </Typography>
        <Divider
          sx={{
            width: '100%',
            marginBottom: '16px',
          }}
        />
      </Grid>

      {showCompanyLoading ? (
        <Grid item xs={12}>
          <Skeleton variant='rounded' height={40} />
        </Grid>
      ) : (
        <>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={handleToggle}
                  disabled={isEnforced || isSaving}
                  sx={PROFILE_SWITCH_SX}
                  inputProps={{'aria-label': t('toggleLabel') as string}}
                />
              }
              label={t(enabled ? 'enabled' : 'disabled') as string}
            />
          </Grid>

          {isEnforced && (
            <Grid item xs={12}>
              <Typography variant='body2' color='text.secondary'>
                {t('enforcedHint')}
              </Typography>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
